import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './client.js';
import type { InvoiceAuditEntry, TenantAuditPage } from './types.js';

export interface AuditPage {
  data: InvoiceAuditEntry[];
  total: number;
}

export function useInvoiceAudit(primaryKey: string) {
  return useQuery<AuditPage>({
    queryKey: ['invoice-audit', primaryKey],
    queryFn: () => apiFetch<AuditPage>(`/invoices/${encodeURIComponent(primaryKey)}/audit`),
    enabled: Boolean(primaryKey),
    staleTime: 15_000,
  });
}

export interface TenantAuditFilters {
  action: string;
  actorUid: string;
  since: string | null;
}

export const EMPTY_TENANT_AUDIT_FILTERS: TenantAuditFilters = {
  action: '',
  actorUid: '',
  since: null,
};

interface UseTenantAuditArgs {
  tenantId: string;
  filters: TenantAuditFilters;
  cursor: string | null;
  enabled?: boolean;
}

function tenantAuditPath(args: UseTenantAuditArgs): string {
  const sp = new URLSearchParams();
  if (args.filters.action) sp.set('action', args.filters.action);
  if (args.filters.actorUid) sp.set('actorUid', args.filters.actorUid);
  if (args.filters.since) sp.set('since', args.filters.since);
  if (args.cursor) sp.set('cursor', args.cursor);
  const qs = sp.toString();
  return `/tenants/${encodeURIComponent(args.tenantId)}/audit${qs ? `?${qs}` : ''}`;
}

export function useTenantAudit(args: UseTenantAuditArgs) {
  return useQuery<TenantAuditPage>({
    queryKey: [
      'tenant-audit',
      args.tenantId,
      args.filters.action,
      args.filters.actorUid,
      args.filters.since,
      args.cursor,
    ],
    queryFn: () => apiFetch<TenantAuditPage>(tenantAuditPath(args)),
    enabled: (args.enabled ?? true) && Boolean(args.tenantId),
    staleTime: 15_000,
  });
}

export function useRefreshTenantAudit() {
  const qc = useQueryClient();
  return (tenantId: string) => {
    void qc.invalidateQueries({ queryKey: ['tenant-audit', tenantId] });
  };
}
